import type React from "react";
import { useState } from "react";

// The single widget for every EHR-workflow interaction (place lab, referral,
// prescription, …). Payload-driven, EHR-agnostic — the backend decides the copy
// and which workflow runs; this component only confirms/answers. Two modes,
// driven by the payload:
//   • confirm mode  — no `questions`: a "run this workflow" CTA → dispatches the
//     ehr_place_order custom action (keyed by recommended_action_id).
//   • input mode    — `questions` present (a paused EhrWorkflowRun needs a
//     clinical decision): an answer form → dispatches the ehr_workflow_respond
//     custom action (keyed by workflow_id).
// Both dispatch through onCustomAction, which streams the placement PROGRESS
// into the chat thread as assistant messages ("placing… → done & confirmed ✓ —
// transfer next?"). So this widget owns only the kick-off: on click it collapses
// to a compact "placing…" chip and hands the conversation to the thread.
export interface EhrWorkflowQuestion {
  key: string;
  prompt: string;
  options?: string[];
  // When true, the clinician can pick several options (e.g. a panel's tests);
  // the answer for this key is sent as a string[]. Defaults to all options.
  multi?: boolean;
}

type Answer = string | string[];

export interface EhrWorkflowPromptData {
  recommended_action_id?: string | null;
  workflow_id?: string | null;
  audio_file_id?: number;
  title?: string;
  ehr_label?: string;
  patient_name?: string;
  patient_mrn?: string;
  question?: string;
  confirm_label?: string;
  questions?: EhrWorkflowQuestion[];
}

interface EhrWorkflowPromptProps {
  data: EhrWorkflowPromptData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
}

// The widget only kicks the run off; progress + outcome stream into the thread.
type Phase =
  | { kind: "confirm" }
  | { kind: "input"; questions: EhrWorkflowQuestion[]; workflowId?: string | null }
  | { kind: "placing" }
  | { kind: "declined" };

const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const defaultsFor = (questions: EhrWorkflowQuestion[]): Record<string, Answer> =>
  Object.fromEntries(
    // Multi defaults to ALL options selected (the recommendation implies the
    // whole set); single defaults to the first option.
    questions.map((q) => [q.key, q.multi ? [...(q.options ?? [])] : q.options?.[0] ?? ""]),
  );

const EhrWorkflowPromptWidget: React.FC<EhrWorkflowPromptProps> = ({ data, onCustomAction, disabled }) => {
  const initialQuestions = data?.questions || [];
  const startsInInput = initialQuestions.length > 0;
  const [phase, setPhase] = useState<Phase>(
    startsInInput ? { kind: "input", questions: initialQuestions, workflowId: data.workflow_id } : { kind: "confirm" },
  );
  const [answers, setAnswers] = useState<Record<string, Answer>>(() => defaultsFor(initialQuestions));

  if (!data?.recommended_action_id && !data?.workflow_id) return null;

  const ehr = data.ehr_label || "your EHR";
  const patientLine = [data.patient_name, data.patient_mrn ? `MRN ${data.patient_mrn}` : ""]
    .filter(Boolean)
    .join(" · ");

  // Both modes fire-and-collapse: dispatch the custom action (which streams the
  // progress + outcome into the thread) and drop to a compact "placing" chip.
  const confirm = () => {
    if (disabled) return;
    onCustomAction({ type: "ehr_place_order", payload: { recommended_action_id: data.recommended_action_id } });
    setPhase({ kind: "placing" });
  };

  const submitAnswers = (workflowId?: string | null) => {
    if (disabled) return;
    onCustomAction({
      type: "ehr_workflow_respond",
      payload: {
        workflow_id: workflowId,
        recommended_action_id: data.recommended_action_id ?? undefined,
        answers,
      },
    });
    setPhase({ kind: "placing" });
  };

  // --- transient / terminal states ----------------------------------------
  if (phase.kind === "placing") {
    return (
      <div style={{ ...styles.container, ...styles.resolved }}>
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>On it — I'll keep you posted in the chat.</span>
        </div>
      </div>
    );
  }
  if (phase.kind === "declined") {
    return (
      <div style={{ ...styles.container, ...styles.resolved }}>
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>No problem — you can run it from chat anytime.</span>
        </div>
      </div>
    );
  }

  // --- input mode: answer the clinical decision ----------------------------
  if (phase.kind === "input") {
    const { questions, workflowId } = phase;
    const answered = (q: EhrWorkflowQuestion): boolean => {
      const v = answers[q.key];
      return q.multi ? Array.isArray(v) && v.length > 0 : (v ?? "").toString().trim() !== "";
    };
    const toggleMulti = (key: string, opt: string) =>
      setAnswers((p) => {
        const cur = Array.isArray(p[key]) ? (p[key] as string[]) : [];
        return { ...p, [key]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
      });
    const allAnswered = questions.every(answered);
    return (
      <div style={styles.container}>
        {questions.map((q) => (
          <div key={q.key} style={styles.qRow}>
            <span style={styles.prompt}>{q.prompt}</span>
            {q.multi && q.options?.length ? (
              <div style={styles.checkGroup}>
                {q.options.map((opt) => (
                  <label key={opt} style={styles.checkRow}>
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={Array.isArray(answers[q.key]) && (answers[q.key] as string[]).includes(opt)}
                      onChange={() => toggleMulti(q.key, opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            ) : q.options?.length ? (
              <select
                disabled={disabled}
                value={(answers[q.key] as string) ?? ""}
                onChange={(e) => setAnswers((p) => ({ ...p, [q.key]: e.target.value }))}
                style={styles.input}
              >
                {q.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                disabled={disabled}
                value={(answers[q.key] as string) ?? ""}
                onChange={(e) => setAnswers((p) => ({ ...p, [q.key]: e.target.value }))}
                style={styles.input}
              />
            )}
          </div>
        ))}
        <button
          type="button"
          disabled={disabled || !allAnswered}
          onClick={() => submitAnswers(workflowId)}
          style={{
            ...styles.button,
            ...styles.confirmButton,
            ...(disabled || !allAnswered ? styles.buttonDisabled : {}),
          }}
        >
          Confirm &amp; continue
        </button>
      </div>
    );
  }

  // --- confirm mode: run this workflow -------------------------------------
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <BoltIcon />
        <span style={styles.headerText}>{data.question || `Run this in ${ehr}?`}</span>
      </div>
      {(data.title || patientLine) && (
        <div style={styles.detail}>
          {data.title}
          {data.title && patientLine ? " — " : ""}
          {patientLine}
        </div>
      )}
      <div style={styles.actionsRow}>
        <button
          type="button"
          disabled={disabled}
          onClick={confirm}
          style={{ ...styles.button, ...styles.confirmButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          {data.confirm_label || "Confirm"}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setPhase({ kind: "declined" })}
          style={{ ...styles.button, ...styles.dismissButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          Skip
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  resolved: { backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" },
  failed: { backgroundColor: "#FEF2F2", border: "1px solid #FECACA" },
  header: { display: "flex", alignItems: "center", gap: "8px" },
  headerText: { fontSize: "13px", fontWeight: 600, color: "#111827", lineHeight: 1.4 },
  detail: { fontSize: "12.5px", color: "#4B5563", lineHeight: 1.4 },
  qRow: { display: "flex", flexDirection: "column", gap: "5px", fontSize: "13px" },
  prompt: { color: "#111827", fontWeight: 500 },
  checkGroup: { display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "2px" },
  checkRow: { display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "#374151" },
  input: {
    padding: "6px 8px",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "13px",
  },
  actionsRow: { display: "flex", gap: "8px", marginTop: "4px" },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirmButton: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  dismissButton: { backgroundColor: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default EhrWorkflowPromptWidget;
